import React from 'react';
import {View} from 'react-native';
import {useStyles} from '../../../../../../shared/hooks/styles';
import {Text} from '../../../../../../shared/components/text';
import {images} from '../../../../../../assets/icons';
import {useTheme} from '../../../../../../shared/theme';
import {ViewProps} from '../models';
import {formatPhone, formatPhoneToCallable} from '../../../../../../shared/pipes';
import {Link} from '../../../../../../shared/components/link';
import {MenuItem, TextField} from '@material-ui/core';
import {Clusterer, Map, Placemark, YMaps} from 'react-yandex-maps';
import MapMarker from '../../../../../../assets/icons/map-marker.svg';
import MapPin from '../../../../../../assets/icons/map-pin.svg';
import {Map as MapType} from 'yandex-maps';

export const DesktopView = ({
  isIssuePointDetailsVisible,
  zoomLevel,
  centerPoint,
  onPressMarker,
  mapData,
  currentMarker,
  setIsIssuePointDetailsVisible,
}: ViewProps) => {
  const [mapIsLoaded, setMapIsLoaded] = React.useState(false);
  const theme = useTheme();
  const styles = useStyles(({addShadow}) => ({
    root: {
      maxWidth: '100%',
      width: '100%',
      position: 'relative',
    },
    mapBlock: {
      overflow: 'hidden',
      borderRadius: 5,
      ...addShadow({height: 2, radius: 18, opacity: 0.1}),
    },
    table: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      paddingTop: 16,
      paddingBottom: 24,
      paddingHorizontal: 26,
      maxHeight: 347,
      overflowY: 'auto',
      flexDirection: 'row',
      zIndex: 1,
      backgroundColor: theme.color.backgroundMenuColor,
      transition: 'transform 0.26s ease-in',
    },
    showTable: {
      transform: [{translateY: 0}],
    },
    hideTable: {
      transform: [{translateY: -250}],
    },
    column: {
      minWidth: 270,
      flex: 1,
    },
    firstColumn: {
      marginRight: 20,
    },
    row: {
      marginBottom: 12,
    },
    rowWithIcon: {
      flexDirection: 'row',
    },
    icon: {
      marginRight: 12,
      color: theme.color.primaryColor,
      justifyContent: 'center',
      height: 30,
    },
    mapWrapper: {
      height: 480,
    },
    map: {
      width: '100%',
      height: '100%',
    },
    groupItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 12,
      paddingVertical: 5,
    },
    issuePointSelectContainer: {
      paddingHorizontal: 24,
      marginBottom: 12,
    },
  }));

  const mapInstance = React.useRef<MapType>();
  const handleMapInstanceChange = React.useCallback((instance: any) => {
    if (!instance || mapInstance.current != null) return;
    mapInstance.current = instance as MapType;
    mapInstance.current.events.add('mousedowndefaultaction', (_e: any) => {
      setIsIssuePointDetailsVisible(false);
    });
  }, []);

  return (
    <View style={styles.root}>
      <View style={styles.issuePointSelectContainer}>
        <TextField
          id='issuepoint-select'
          label='Выбрать пункт выдачи'
          variant='outlined'
          select
          value={currentMarker?.id ?? ''}>
          {mapData.map((issuePoint) => (
            <MenuItem key={issuePoint.id} onClick={() => onPressMarker(issuePoint)} value={issuePoint.id}>
              {issuePoint.address || issuePoint.name}
            </MenuItem>
          ))}
        </TextField>
      </View>

      <View style={styles.mapBlock}>
        {isIssuePointDetailsVisible && currentMarker != null && (
          <View style={[styles.table, styles[currentMarker ? 'showTable' : 'hideTable']]}>
            <View style={[styles.column, styles.firstColumn]}>
              <Text style={styles.row} type='h6'>
                Отделение {currentMarker.name || currentMarker.organization.name}
              </Text>
              <View style={[styles.row, styles.rowWithIcon]}>
                <View style={styles.icon}>
                  <images.Location width={24} height={24} color={theme.color.primaryColor} />
                </View>
                <Text type='h5Light'>{currentMarker?.address}</Text>
              </View>
              <View style={styles.rowWithIcon}>
                <Link
                  testID='phone-geo-map-link-id'
                  href={`tel:${formatPhoneToCallable(currentMarker.phone)}`}
                  external
                  style={styles.groupItem}>
                  <View style={styles.icon}>
                    <images.Phone width={24} height={24} color={theme.color.primaryColor} />
                  </View>
                  <Text type='h5Light'>{formatPhone(currentMarker.phone)}</Text>
                </Link>
              </View>
              <View style={styles.groupItem}>
                <View style={styles.icon}>
                  <images.Cards width={24} height={24} color={theme.color.primaryColor} />
                </View>
                <Text type='h5Light'>Карт в наличии {currentMarker?.availableCardsCount}</Text>
              </View>
            </View>
            <View style={styles.column}>
              <Text style={styles.row} type='h6'>
                Часы работы
              </Text>
              <View style={styles.rowWithIcon}>
                <View style={styles.icon}>
                  <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
                </View>
                <Text type='h5Light'>{currentMarker.operatingMode}</Text>
              </View>
            </View>
          </View>
        )}
        <View style={styles.mapWrapper}>
          <YMaps>
            <Map
              instanceRef={handleMapInstanceChange}
              options={{autoFitToViewport: 'always'}}
              height='100%'
              width='100%'
              state={{center: centerPoint, zoom: zoomLevel || 9}}
              defaultState={{center: centerPoint, zoom: (zoomLevel || 9) - 0.8}}
              onLoad={() => setMapIsLoaded(true)}>
              {mapIsLoaded && (
                <Clusterer options={{preset: 'islands#invertedOrangeClusterIcons'}}>
                  {mapData.map((marker) => (
                    <Placemark
                      key={marker.id}
                      geometry={marker.geolocation ?? undefined}
                      onClick={() => onPressMarker(marker)}
                      options={{
                        iconLayout: 'default#image',
                        iconImageHref: marker === currentMarker ? MapMarker : MapPin,
                        iconImageSize: [39, 39],
                        iconImageOffset: [-30, -60],
                      }}
                    />
                  ))}
                </Clusterer>
              )}
            </Map>
          </YMaps>
        </View>
      </View>
    </View>
  );
};
